import ChevronLeft from "@/src/assets/icons/icon-chevron-left-black.svg";
import { ReactComponent as EditSvg } from "@/src/assets/icons/icon-edit-pencel.svg";
import Badge from "@/src/components/atom/Badge";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Loader from "@/src/components/atom/Loader";
import Typo from "@/src/components/atom/Typo";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import ImporterMainLayout from "@/src/components/template/Layout/importer/ImporterMainLayout";
import { LANGUAGE_LIST } from "@/src/constant/optionList";
import IMPORTER_PRIVATE_PATH from "@/src/routes/importer/path";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { ExporterUserType } from "@/src/store/apis/auth/interface";
import { useGetStaffsQuery } from "@/src/store/apis/corporate/staffManagement";
import colorSet from "@/src/styles/color";
import { isUndefined } from "@/src/utils/is";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import WithdrawDialog from "./dialog/WithdrawDialog";

export const getExporterUserType = (type: ExporterUserType, t: TFunction) => {
  if (type === "CORPORATE_MANAGER") {
    return t("myPage:importer.optionValue.corporateManager");
  }
  if (type === "MIDDLE_MANAGER") {
    return t("myPage:importer.optionValue.middleManager");
  }
  if (type === "MANAGER") {
    return t("myPage:importer.optionValue.manager");
  }
};

enum DialogState {
  NULL,
  WITHDRAW,
}

const ImporterMypage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);

  // API
  const {
    name,
    email,
    companyName,
    personalContact,
    officeContact,
    category,
    isMarketingEmailReceive,
    exporterUserType,
    notification,
    isMfaOn,
    language,
    isFetching,
    refetch: refetchSession,
  } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isUndefined(currentData) || isError || isFetching;

      const notificationStrings = !isUnstable
        ? [
            currentData.row.account.notificationEmailReceive
              ? t("common:email.en")
              : "",
            currentData.row.account.notificationSmsReceive
              ? t("common:sms.en")
              : "",
            currentData.row.account.notificationWebPushReceive
              ? t("common:webPush.en")
              : "",
          ]
            .filter(Boolean)
            .join(", ") || "-"
        : "-";

      return {
        name: !isUnstable ? currentData?.row.name : "-",
        email: !isUnstable ? currentData?.row.email : "-",
        companyName: !isUnstable ? currentData?.row.exporter.companyName : "-",
        personalContact:
          !isUnstable &&
          currentData?.row?.personalContact &&
          currentData?.row?.personalContactPrefix
            ? `${currentData?.row?.personalContactPrefix} ${currentData?.row?.personalContact}`
            : "-",
        officeContact:
          !isUnstable &&
          currentData?.row?.officeContact &&
          currentData?.row?.officeContactPrefix
            ? `${currentData?.row?.officeContactPrefix} ${currentData?.row?.officeContact}`
            : "-",
        category: !isUnstable
          ? currentData?.row.mainCategoryCodeItemNames.join(", ")
          : "-",
        isMarketingEmailReceive: !isUnstable
          ? currentData?.row.account.isMarketingEmailReceive
          : false,
        exporterUserType: !isUnstable
          ? getExporterUserType(currentData.row.exporterUserType, t)
          : "-",
        notification: notificationStrings,
        isMfaOn: !isUnstable ? currentData?.row.account.isMfaOn : isUnstable,
        language: currentData?.row.account.language,
        isFetching,
      };
    },
  });

  useGetStaffsQuery(
    {
      isActivated: true,
      exporterUserType: "CORPORATE_MANAGER",
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const informationSet = [
    { label: t("myPage:importer.label.name"), value: name },
    { label: t("myPage:importer.label.id"), value: email },
    {
      label: t("myPage:importer.label.affiliatedCompany"),
      value: companyName,
    },
    {
      label: t("myPage:importer.label.authority"),
      value: exporterUserType,
    },
    { label: t("myPage:importer.label.password"), value: "••••••••" },
    {
      label: t("myPage:importer.label.contactPersonal"),
      value: personalContact,
    },
    {
      label: t("myPage:importer.label.contactOffice"),
      value: officeContact,
    },
  ];

  const informationRightSet = [
    {
      label: t("myPage:importer.label.assignedCategory"),
      value: category,
    },
    {
      label: t("myPage:importer.label.notification"),
      value: notification,
    },
    {
      label: t("myPage:importer.label.agreeToReceiveEmail"),
      value: isMarketingEmailReceive ? (
        <FlexCenter>
          <Badge
            badgeColor="systemLime5"
            color="systemLime1"
            badgeSize="S"
            text={t("myPage:importer.optionValue.agree")}
          />
        </FlexCenter>
      ) : (
        <FlexCenter>
          <Badge
            badgeColor="gray10"
            color="gray7"
            badgeSize="S"
            text={t("myPage:importer.optionValue.disagree")}
          />
        </FlexCenter>
      ),
    },
    {
      label: t("myPage:importer.label.twoFactorAuthentication"),
      value: isMfaOn ? (
        <FlexCenter>
          <Badge
            badgeColor="systemLime5"
            color="systemLime1"
            badgeSize="S"
            text="ON"
          />
        </FlexCenter>
      ) : (
        <FlexCenter>
          <Badge badgeColor="gray10" color="gray7" badgeSize="S" text="OFF" />
        </FlexCenter>
      ),
    },
    {
      label: t("common:languageSetting"),
      value: t(
        LANGUAGE_LIST.find((item) => item.value === language)?.label || "",
      ),
    },
  ];

  const renderDialog = () => {
    if (dialogState === DialogState.NULL) {
      return null;
    }

    if (dialogState === DialogState.WITHDRAW) {
      return (
        <WithdrawDialog
          open={dialogState === DialogState.WITHDRAW}
          onOpenChange={() => setDialogState(DialogState.NULL)}
        />
      );
    }
  };

  useEffect(() => {
    if (i18n.language) {
      refetchSession();
    }
  }, [i18n.language, refetchSession]);

  return (
    <ImporterMainLayout
      breadcrumb={[t("sideNav:myPage")]}
      customPageTitle={
        <>
          <StyledHeader>
            {
              <Typo typoType="h1" as="h1" color="gray1">
                {t("sideNav:myPage")}
              </Typo>
            }

            <StyledButton
              buttonSize={32}
              onClick={() => navigate(IMPORTER_PRIVATE_PATH.MY_PAGE_EDIT)}
            >
              <EditIcon />
              {t("myPage:importer.edit")}
            </StyledButton>
          </StyledHeader>
        </>
      }
    >
      <MypageLayout>
        <SectionCard cardTitle={t("myPage:importer.label.information")}>
          {isFetching ? (
            <LoaderBox>
              <Loader size={40} />
            </LoaderBox>
          ) : (
            <FlexBox>
              <HalfBox>
                {informationSet.map(({ label, value }) => {
                  return <SectionCardRow key={label} label={label} value={value} />;
                })}
              </HalfBox>

              <GrayLine />

              <HalfBox>
                {informationRightSet.map(({ label, value }) => {
                  return <SectionCardRow key={label} label={label} value={value} />;
                })}
              </HalfBox>
            </FlexBox>
          )}
        </SectionCard>

        <WithdrawButtonContainer>
          <WithdrawButton
            buttonColor="black"
            buttonGrade="tertiary"
            buttonSize={32}
            onClick={() => setDialogState(DialogState.WITHDRAW)}
          >
            {t("myPage:importer.button.withdraw")}
          </WithdrawButton>
        </WithdrawButtonContainer>
      </MypageLayout>

      {/* Bottom Fixed */}
      <BottomFixedContainer>
        <PreviousButton
          buttonColor="black"
          buttonGrade="tertiary"
          onClick={() => navigate(-1)}
        >
          <Icon iconSrc={ChevronLeft} iconSize={16} />
          {t("myPage:importer.button.backToPreviousButton")}
        </PreviousButton>
      </BottomFixedContainer>

      {renderDialog()}
    </ImporterMainLayout>
  );
};

export default ImporterMypage;

const MypageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const WithdrawButton = styled(StyledButton)`
  margin-left: auto;
`;
const PreviousButton = styled(StyledButton)`
  gap: 4px;
`;

const EditIcon = styled(EditSvg)`
  path {
    fill: ${colorSet.white};
  }
`;

const FlexBox = styled.div`
  width: 100%;
  display: flex;
  gap: 24px;
`;

const HalfBox = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(50% - 12px);
  gap: 24px;
`;

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const WithdrawButtonContainer = styled.div``;

const GrayLine = styled.div`
  border-right: 1px solid ${colorSet.gray9};
`;

const LoaderBox = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
